// utils/eira/TrustSignalsManager.ts
// Advanced trust signal management for optimizing reCAPTCHA challenges

interface DeviceInfo {
  userAgent: string;
  platform: string;
  language: string;
  languages: string[];
  screenResolution: string;
  colorDepth: number;
  timezone: string;
  cookieEnabled: boolean;
  doNotTrack: string | null;
  hardwareConcurrency: number;
  deviceMemory?: number;
  touchSupport: boolean;
  orientationSupport: boolean;
}

interface NetworkInfo {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
  onLine: boolean;
}

interface BehaviorSignals {
  mouseMovements: number;
  keystrokes: number;
  scrollEvents: number;
  focusEvents: number;
  visibilityChanges: number;
  touchEvents: number;
  sessionDuration: number;
  pageViews: number;
  interactionScore: number;
}

interface AuthHistory {
  loginCount: number;
  consecutiveSuccessfulLogins: number;
  lastSuccessfulLogin?: string;
  failedAttempts: number;
  otpSuccessRate: number;
  recaptchaSuccessRate: number;
  preferredAuthMethod: string;
  accountAge?: number;
}

interface TrustScore {
  overall: number;
  device: number;
  behavior: number;
  history: number;
  network: number;
  calculated: string;
  factors: string[];
}

class TrustSignalsManager {
  private static instance: TrustSignalsManager | null = null;
  private behaviorSignals: BehaviorSignals;
  private deviceInfo: DeviceInfo | null = null;
  private networkInfo: NetworkInfo | null = null;
  private authHistory: AuthHistory | null = null;
  private isTracking: boolean = false;
  private sessionStartTime: number;
  private interactionTimeouts: Map<string, NodeJS.Timeout> = new Map();

  private constructor() {
    this.sessionStartTime = Date.now();
    this.behaviorSignals = {
      mouseMovements: 0,
      keystrokes: 0,
      scrollEvents: 0,
      focusEvents: 0,
      visibilityChanges: 0,
      touchEvents: 0,
      sessionDuration: 0,
      pageViews: 1,
      interactionScore: 0,
    };

    this.initializeTracking();
  }

  /**
   * Singleton instance getter
   */
  public static getInstance(): TrustSignalsManager {
    if (!TrustSignalsManager.instance) {
      TrustSignalsManager.instance = new TrustSignalsManager();
    }
    return TrustSignalsManager.instance;
  }

  /**
   * Initialize comprehensive tracking
   */
  private initializeTracking(): void {
    try {
      this.collectDeviceInfo();
      this.collectNetworkInfo();
      this.loadAuthHistory();
      this.startBehaviorTracking();
      this.setupPeriodicUpdates();

    } catch (error) {
      console.warn("Failed to initialize trust signals tracking:", error);
    }
  }

  /**
   * Collect comprehensive device information
   */
  private collectDeviceInfo(): void {
    try {
      this.deviceInfo = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        languages: navigator.languages
          ? Array.from(navigator.languages)
          : [navigator.language],
        screenResolution: `${screen.width}x${screen.height}`,
        colorDepth: screen.colorDepth,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        cookieEnabled: navigator.cookieEnabled,
        doNotTrack: navigator.doNotTrack,
        hardwareConcurrency: navigator.hardwareConcurrency || 0,
        deviceMemory: (navigator as any).deviceMemory,
        touchSupport: "ontouchstart" in window || navigator.maxTouchPoints > 0,
        orientationSupport: "orientation" in window,
      };

      // Store device info for persistence
      localStorage.setItem("device_info_v2", JSON.stringify(this.deviceInfo));
    } catch (error) {
      console.warn("Failed to collect device info:", error);
    }
  }

  /**
   * Collect network information
   */
  private collectNetworkInfo(): void {
    try {
      const connection =
        (navigator as any).connection ||
        (navigator as any).mozConnection ||
        (navigator as any).webkitConnection;

      this.networkInfo = {
        effectiveType: connection?.effectiveType,
        downlink: connection?.downlink,
        rtt: connection?.rtt,
        saveData: connection?.saveData,
        onLine: navigator.onLine,
      };

      // Listen for network changes
      window.addEventListener("online", () => {
        if (this.networkInfo) this.networkInfo.onLine = true;
        this.updateInteractionScore(5); // Bonus for network recovery
      });

      window.addEventListener("offline", () => {
        if (this.networkInfo) this.networkInfo.onLine = false;
      });

      if (connection) {
        connection.addEventListener("change", () => {
          this.collectNetworkInfo();
        });
      }
    } catch (error) {
      console.warn("Failed to collect network info:", error);
      this.networkInfo = { onLine: navigator.onLine };
    }
  }

  /**
   * Load authentication history from localStorage
   */
  private loadAuthHistory(): void {
    try {
      const stored = localStorage.getItem("auth_history_v2");
      if (stored) {
        this.authHistory = JSON.parse(stored);
      } else {
        this.authHistory = {
          loginCount: parseInt(localStorage.getItem("login_count") || "0"),
          consecutiveSuccessfulLogins: parseInt(
            localStorage.getItem("consecutive_successful_logins") || "0"
          ),
          lastSuccessfulLogin:
            localStorage.getItem("last_successful_login") || undefined,
          failedAttempts: parseInt(
            localStorage.getItem("failed_attempts") || "0"
          ),
          otpSuccessRate: this.calculateSuccessRate(
            "otp_verify_count",
            "otp_send_count"
          ),
          recaptchaSuccessRate: this.calculateSuccessRate(
            "recaptcha_success_count",
            "recaptcha_error_count"
          ),
          preferredAuthMethod:
            localStorage.getItem("preferred_auth_method") || "phone",
          accountAge: this.calculateAccountAge(),
        };

        this.saveAuthHistory();
      }
    } catch (error) {
      console.warn("Failed to load auth history:", error);
      this.authHistory = {
        loginCount: 0,
        consecutiveSuccessfulLogins: 0,
        failedAttempts: 0,
        otpSuccessRate: 0,
        recaptchaSuccessRate: 0,
        preferredAuthMethod: "phone",
      };
    }
  }

  /**
   * Calculate success rate from localStorage counters
   */
  private calculateSuccessRate(successKey: string, totalKey: string): number {
    const success = parseInt(localStorage.getItem(successKey) || "0");
    const total = parseInt(localStorage.getItem(totalKey) || "0");
    return total > 0 ? (success / total) * 100 : 0;
  }

  /**
   * Calculate account age in days
   */
  private calculateAccountAge(): number | undefined {
    const firstVisit = localStorage.getItem("first_visit_time");
    if (firstVisit) {
      const daysDiff =
        (Date.now() - new Date(firstVisit).getTime()) / (1000 * 60 * 60 * 24);
      return Math.floor(daysDiff);
    }
    return undefined;
  }

  /**
   * Start behavioral tracking
   */
  private startBehaviorTracking(): void {
    if (this.isTracking) return;

    this.isTracking = true;

    // Mouse movement tracking
    let mouseMovementCount = 0;
    const mouseHandler = () => {
      mouseMovementCount++;
      if (mouseMovementCount <= 100) {
        // Cap to prevent overflow
        this.behaviorSignals.mouseMovements = mouseMovementCount;
        this.updateInteractionScore(0.1);
      }
    };
    document.addEventListener("mousemove", mouseHandler, { passive: true });

    // Keystroke tracking
    document.addEventListener(
      "keydown",
      () => {
        this.behaviorSignals.keystrokes++;
        this.updateInteractionScore(0.5);
      },
      { passive: true }
    );

    // Scroll tracking
    document.addEventListener(
      "scroll",
      () => {
        this.behaviorSignals.scrollEvents++;
        this.updateInteractionScore(0.2);
      },
      { passive: true }
    );

    // Focus tracking
    window.addEventListener("focus", () => {
      this.behaviorSignals.focusEvents++;
      this.updateInteractionScore(1);
    });

    // Visibility change tracking
    document.addEventListener("visibilitychange", () => {
      this.behaviorSignals.visibilityChanges++;
      if (!document.hidden) {
        this.updateInteractionScore(2);
      }
    });

    // Touch event tracking
    ["touchstart", "touchmove", "touchend"].forEach((event) => {
      document.addEventListener(
        event,
        () => {
          this.behaviorSignals.touchEvents++;
          this.updateInteractionScore(0.3);
        },
        { passive: true }
      );
    });

    // Page view tracking
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function (...args) {
      originalPushState.apply(history, args);
      TrustSignalsManager.getInstance().incrementPageViews();
    };

    history.replaceState = function (...args) {
      originalReplaceState.apply(history, args);
      TrustSignalsManager.getInstance().incrementPageViews();
    };

    window.addEventListener("popstate", () => {
      this.incrementPageViews();
    });
  }

  /**
   * Update interaction score with debouncing
   */
  private updateInteractionScore(points: number): void {
    this.behaviorSignals.interactionScore = Math.min(
      this.behaviorSignals.interactionScore + points,
      1000 // Cap at 1000
    );

    // Debounce localStorage updates
    if (this.interactionTimeouts.has("score")) {
      clearTimeout(this.interactionTimeouts.get("score")!);
    }

    this.interactionTimeouts.set(
      "score",
      setTimeout(() => {
        localStorage.setItem(
          "user_interaction_score",
          this.behaviorSignals.interactionScore.toString()
        );
        localStorage.setItem("human_behavior_detected", "true");
        localStorage.setItem("last_interaction", new Date().toISOString());
      }, 1000)
    );
  }

  /**
   * Increment page views
   */
  private incrementPageViews(): void {
    this.behaviorSignals.pageViews++;
    this.updateInteractionScore(5);
  }

  /**
   * Set up periodic updates
   */
  private setupPeriodicUpdates(): void {
    setInterval(() => {
      this.behaviorSignals.sessionDuration = Date.now() - this.sessionStartTime;
      this.saveBehaviorSignals();
    }, 30000); // Every 30 seconds

    // Save on page unload
    window.addEventListener("beforeunload", () => {
      this.saveBehaviorSignals();
      this.saveAuthHistory();
    });
  }

  /**
   * Save behavior signals to localStorage
   */
  private saveBehaviorSignals(): void {
    try {
      localStorage.setItem(
        "behavior_signals_v2",
        JSON.stringify(this.behaviorSignals)
      );
    } catch (error) {
      console.warn("Failed to save behavior signals:", error);
    }
  }

  /**
   * Save auth history to localStorage
   */
  private saveAuthHistory(): void {
    try {
      if (this.authHistory) {
        localStorage.setItem(
          "auth_history_v2",
          JSON.stringify(this.authHistory)
        );
      }
    } catch (error) {
      console.warn("Failed to save auth history:", error);
    }
  }

  /**
   * Record successful authentication
   */
  public recordSuccessfulAuth(method: string, details?: any): void {
    if (!this.authHistory) return;

    this.authHistory.loginCount++;
    this.authHistory.consecutiveSuccessfulLogins++;
    this.authHistory.lastSuccessfulLogin = new Date().toISOString();
    this.authHistory.failedAttempts = 0; // Reset failed attempts
    this.authHistory.preferredAuthMethod = method;

    // Update success rates
    this.authHistory.otpSuccessRate = this.calculateSuccessRate(
      "otp_verify_count",
      "otp_send_count"
    );
    this.authHistory.recaptchaSuccessRate = this.calculateSuccessRate(
      "recaptcha_success_count",
      "recaptcha_error_count"
    );

    // Bonus interaction score for successful auth
    this.updateInteractionScore(50);

    this.saveAuthHistory();
  }

  /**
   * Record failed authentication
   */
  public recordFailedAuth(method: string, reason?: string): void {
    if (!this.authHistory) return;

    this.authHistory.failedAttempts++;
    this.authHistory.consecutiveSuccessfulLogins = 0; // Reset consecutive successes

    // Update success rates
    this.authHistory.otpSuccessRate = this.calculateSuccessRate(
      "otp_verify_count",
      "otp_send_count"
    );
    this.authHistory.recaptchaSuccessRate = this.calculateSuccessRate(
      "recaptcha_success_count",
      "recaptcha_error_count"
    );

    this.saveAuthHistory();
  }

  /**
   * Calculate comprehensive trust score
   */
  public calculateTrustScore(): TrustScore {
    let deviceScore = 0;
    let behaviorScore = 0;
    let historyScore = 0;
    let networkScore = 0;
    const factors: string[] = [];

    // Device trust factors (0-25 points)
    if (this.deviceInfo) {
      deviceScore += 5; // Base score for having device info

      if (this.deviceInfo.cookieEnabled) {
        deviceScore += 3;
        factors.push("cookies_enabled");
      }

      if (
        !this.deviceInfo.doNotTrack ||
        this.deviceInfo.doNotTrack === "unspecified"
      ) {
        deviceScore += 2;
        factors.push("tracking_allowed");
      }

      if (this.deviceInfo.hardwareConcurrency > 1) {
        deviceScore += 3;
        factors.push("modern_hardware");
      }

      if (this.deviceInfo.touchSupport) {
        deviceScore += 2;
        factors.push("touch_capable");
      }

      // Language consistency
      if (this.deviceInfo.languages.length > 1) {
        deviceScore += 2;
        factors.push("multilingual");
      }

      // Screen resolution reasonableness
      const [width, height] = this.deviceInfo.screenResolution
        .split("x")
        .map(Number);
      if (width >= 1024 && height >= 768) {
        deviceScore += 3;
        factors.push("standard_resolution");
      }

      // Timezone consistency
      const expectedTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (this.deviceInfo.timezone === expectedTZ) {
        deviceScore += 5;
        factors.push("timezone_consistent");
      }
    }

    // Behavior trust factors (0-30 points)
    if (this.behaviorSignals.interactionScore > 0) {
      behaviorScore += Math.min(10, this.behaviorSignals.interactionScore / 10);
      factors.push("user_interaction");
    }

    if (this.behaviorSignals.mouseMovements > 10) {
      behaviorScore += 5;
      factors.push("natural_mouse_movement");
    }

    if (this.behaviorSignals.keystrokes > 5) {
      behaviorScore += 3;
      factors.push("keyboard_input");
    }

    if (this.behaviorSignals.sessionDuration > 30000) {
      // 30 seconds
      behaviorScore += Math.min(
        7,
        this.behaviorSignals.sessionDuration / 60000
      ); // 1 point per minute, max 7
      factors.push("session_duration");
    }

    if (this.behaviorSignals.pageViews > 1) {
      behaviorScore += Math.min(5, this.behaviorSignals.pageViews);
      factors.push("page_navigation");
    }

    // Authentication history trust factors (0-30 points)
    if (this.authHistory) {
      if (this.authHistory.loginCount > 0) {
        historyScore += Math.min(10, this.authHistory.loginCount);
        factors.push("login_history");
      }

      if (this.authHistory.consecutiveSuccessfulLogins > 0) {
        historyScore += Math.min(
          8,
          this.authHistory.consecutiveSuccessfulLogins * 2
        );
        factors.push("consecutive_success");
      }

      if (this.authHistory.otpSuccessRate > 80) {
        historyScore += 5;
        factors.push("high_otp_success");
      }

      if (this.authHistory.recaptchaSuccessRate > 90) {
        historyScore += 4;
        factors.push("high_recaptcha_success");
      }

      if (this.authHistory.accountAge && this.authHistory.accountAge > 7) {
        historyScore += Math.min(3, this.authHistory.accountAge / 30); // 1 point per month, max 3
        factors.push("account_age");
      }
    }

    // Network trust factors (0-15 points)
    if (this.networkInfo) {
      if (this.networkInfo.onLine) {
        networkScore += 5;
        factors.push("network_connected");
      }

      if (
        this.networkInfo.effectiveType &&
        ["4g", "3g"].includes(this.networkInfo.effectiveType)
      ) {
        networkScore += 3;
        factors.push("good_connection");
      }

      if (this.networkInfo.rtt && this.networkInfo.rtt < 100) {
        networkScore += 2;
        factors.push("low_latency");
      }

      if (this.networkInfo.downlink && this.networkInfo.downlink > 1) {
        networkScore += 2;
        factors.push("good_bandwidth");
      }

      if (!this.networkInfo.saveData) {
        networkScore += 3;
        factors.push("full_quality_preferred");
      }
    }

    const overall = Math.min(
      100,
      deviceScore + behaviorScore + historyScore + networkScore
    );

    return {
      overall,
      device: Math.min(25, deviceScore),
      behavior: Math.min(30, behaviorScore),
      history: Math.min(30, historyScore),
      network: Math.min(15, networkScore),
      calculated: new Date().toISOString(),
      factors,
    };
  }

  /**
   * Get trust signals for reCAPTCHA
   */
  public getTrustSignalsForRecaptcha(): any {
    const trustScore = this.calculateTrustScore();

    return {
      trustScore: trustScore.overall,
      deviceInfo: this.deviceInfo,
      behaviorSignals: this.behaviorSignals,
      authHistory: this.authHistory,
      networkInfo: this.networkInfo,
      factors: trustScore.factors,
      riskLevel: this.calculateRiskLevel(trustScore.overall),
      sessionId: this.generateSessionId(),
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Calculate risk level based on trust score
   */
  private calculateRiskLevel(score: number): "low" | "medium" | "high" {
    if (score >= 70) return "low";
    if (score >= 40) return "medium";
    return "high";
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    const stored = sessionStorage.getItem("trust_session_id");
    if (stored) return stored;

    const sessionId = `ts_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    sessionStorage.setItem("trust_session_id", sessionId);
    return sessionId;
  }

  /**
   * Export all data for debugging
   */
  public exportDebugData(): any {
    return {
      deviceInfo: this.deviceInfo,
      networkInfo: this.networkInfo,
      behaviorSignals: this.behaviorSignals,
      authHistory: this.authHistory,
      trustScore: this.calculateTrustScore(),
      sessionDuration: Date.now() - this.sessionStartTime,
      isTracking: this.isTracking,
    };
  }

  /**
   * Reset all tracking data (for testing or privacy compliance)
   */
  public resetAllData(): void {
    // Clear localStorage
    const keysToRemove = [
      "device_info_v2",
      "behavior_signals_v2",
      "auth_history_v2",
      "user_interaction_score",
      "human_behavior_detected",
      "last_interaction",
    ];

    keysToRemove.forEach((key) => localStorage.removeItem(key));

    // Clear sessionStorage
    sessionStorage.removeItem("trust_session_id");

    // Reset instance data
    this.behaviorSignals = {
      mouseMovements: 0,
      keystrokes: 0,
      scrollEvents: 0,
      focusEvents: 0,
      visibilityChanges: 0,
      touchEvents: 0,
      sessionDuration: 0,
      pageViews: 1,
      interactionScore: 0,
    };

    this.deviceInfo = null;
    this.networkInfo = null;
    this.authHistory = null;
    this.sessionStartTime = Date.now();

    // Reinitialize
    this.initializeTracking();

  }
}

// Export singleton instance
export default TrustSignalsManager.getInstance();
